/**
 * WebMCP (https://github.com/webmachinelearning/webmcp) integration.
 *
 * The page exposes client side actions as tools that a browser agent can discover and invoke.
 * The specced entry point is `document.modelContext.registerTool(descriptor, { signal })`,
 * while polyfills and earlier drafts expose `navigator.modelContext` and/or a `provideContext({ tools })`
 * method that replaces the whole tool list at once. Both shapes are supported here.
 *
 * The API is not implemented by every browser (and an extension based provider may be injected after
 * our bundle runs), so tools are kept in an internal registry and attached to the provider as soon as
 * one shows up. When no provider ever appears the whole thing stays inert - no errors, no UI impact.
 */

const PROVIDER_POLL_INTERVAL_MS = 500;
const PROVIDER_POLL_TIMEOUT_MS = 15000;

const resolveProvider = () => {
    const documentProvider = typeof document === 'undefined' ? null : document.modelContext;
    const navigatorProvider = typeof navigator === 'undefined' ? null : navigator.modelContext;

    return documentProvider || navigatorProvider || null;
};

const toToolResult = result => {
    const isAlreadyToolResult = Boolean(result) && Array.isArray(result.content);

    let text = 'Done.';

    if (result !== undefined && result !== null) {
        text = typeof result === 'string' ? result : JSON.stringify(result);
    }

    return isAlreadyToolResult ? result : { content: [{ type: 'text', text }] };
};

const toErrorResult = error => ({
    isError: true,
    content: [{ type: 'text', text: error?.message || 'The tool call failed.' }]
});

class WebMcpService {
    constructor({
        pollIntervalMs = PROVIDER_POLL_INTERVAL_MS,
        pollTimeoutMs = PROVIDER_POLL_TIMEOUT_MS
    } = {}) {
        this.tools = new Map();
        this.pollIntervalMs = pollIntervalMs;
        this.pollTimeoutMs = pollTimeoutMs;
        this.pollTimerId = null;
        this.pollElapsedMs = 0;
    }

    get provider() {
        return resolveProvider();
    }

    get isSupported() {
        return Boolean(this.provider);
    }

    /**
     * @param {{ name: string, description: string, inputSchema: object, execute: Function }} descriptor
     * @returns {Function} call it to unregister the tool
     */
    registerTool(descriptor) {
        if (!descriptor?.name || typeof descriptor.execute !== 'function') {
            throw new Error('A WebMCP tool requires a "name" and an "execute" callback.');
        }

        const { name } = descriptor;

        if (this.tools.has(name)) {
            this.unregisterTool(name);
        }

        this.tools.set(name, {
            descriptor: { ...descriptor, execute: this.wrapExecute(descriptor) },
            controller: null,
            detach: null,
            isAttached: false
        });

        this.syncProvider();

        return () => this.unregisterTool(name);
    }

    /**
     * @param {Array} descriptors
     * @returns {Function} call it to unregister all of them
     */
    registerTools(descriptors = []) {
        const unregisterCallbacks = descriptors.map(descriptor => this.registerTool(descriptor));

        return () => unregisterCallbacks.forEach(unregister => unregister());
    }

    unregisterTool(name) {
        const tool = this.tools.get(name);

        if (tool) {
            this.tools.delete(name);

            try {
                tool.controller?.abort();

                tool.detach?.();

                if (tool.isAttached && typeof this.provider?.unregisterTool === 'function') {
                    this.provider.unregisterTool(name);
                }
            } catch (error) {
                console.warn(`We were not able to unregister the WebMCP tool "${name}".`, error);
            }

            this.provideContextIfSupported();
        }
    }

    /**
     * The tools currently registered by the page, in their WebMCP descriptor shape.
     */
    getToolDescriptors() {
        return [...this.tools.values()].map(({ descriptor }) => descriptor);
    }

    /**
     * Runs a registered tool in place - meant for in page agents and for trying tools out from the
     * console when the browser has no WebMCP provider of its own.
     */
    callTool(name, args = {}) {
        const tool = this.tools.get(name);

        return tool
            ? tool.descriptor.execute(args)
            : Promise.resolve(toErrorResult(new Error(`There is no "${name}" tool registered.`)));
    }

    wrapExecute({ name, execute }) {
        return async (...args) => {
            let result;

            try {
                result = toToolResult(await execute(...args));
            } catch (error) {
                console.error(`WebMCP tool "${name}" failed.`, error);

                result = toErrorResult(error);
            }

            return result;
        };
    }

    syncProvider() {
        const { provider } = this;

        if (!provider) {
            this.pollForProvider();
        } else {
            this.stopPollingForProvider();

            if (typeof provider.registerTool === 'function') {
                this.tools.forEach(tool => this.attachTool(provider, tool));
            } else {
                this.provideContextIfSupported();
            }
        }
    }

    attachTool(provider, tool) {
        if (!tool.isAttached) {
            // Optimistically marked as attached - `registerTool()` resolves asynchronously and we must
            // not register the same tool twice while it is in flight.
            tool.isAttached = true;

            try {
                const controller = typeof AbortController === 'undefined' ? null : new AbortController();

                tool.controller = controller;

                const result = provider.registerTool(
                    tool.descriptor,
                    controller ? { signal: controller.signal } : undefined
                );

                // Polyfills may hand back an unregister callback instead of relying on the abort signal.
                if (typeof result === 'function') {
                    tool.detach = result;
                }

                Promise.resolve(result).catch(error => {
                    tool.isAttached = false;

                    console.warn(`We were not able to register the WebMCP tool "${tool.descriptor.name}".`, error);
                });
            } catch (error) {
                tool.isAttached = false;

                console.warn(`We were not able to register the WebMCP tool "${tool.descriptor.name}".`, error);
            }
        }
    }

    provideContextIfSupported() {
        const { provider } = this;

        const isProvideContextMode = Boolean(provider)
            && typeof provider.provideContext === 'function'
            && typeof provider.registerTool !== 'function';

        if (isProvideContextMode) {
            try {
                provider.provideContext({
                    tools: [...this.tools.values()].map(({ descriptor }) => descriptor)
                });

                this.tools.forEach(tool => { tool.isAttached = true; });
            } catch (error) {
                console.warn('We were not able to provide the WebMCP tools.', error);
            }
        }
    }

    pollForProvider() {
        if (!this.pollTimerId && typeof window !== 'undefined') {
            this.pollElapsedMs = 0;

            this.pollTimerId = window.setInterval(() => {
                this.pollElapsedMs += this.pollIntervalMs;

                if (this.provider) {
                    this.syncProvider();
                } else if (this.pollElapsedMs >= this.pollTimeoutMs) {
                    this.stopPollingForProvider();
                }
            }, this.pollIntervalMs);
        }
    }

    stopPollingForProvider() {
        if (this.pollTimerId) {
            window.clearInterval(this.pollTimerId);

            this.pollTimerId = null;
        }
    }
}

export { WebMcpService };

export default new WebMcpService();